import { datadogLogs } from '@datadog/browser-logs';
import type {
  SalaryCurrency,
  SalaryFrequency,
} from '@seek/salary-data/lib-types/types';
import { useState, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { actionResetMaxSalary } from 'src/app/state/modules/formData/maxSalary';
import { actionResetMinSalary } from 'src/app/state/modules/formData/minSalary';
import { actionUpdatePayType } from 'src/app/state/modules/formData/payType';
import { actionUpdateSalaryCurrency } from 'src/app/state/modules/formData/salaryCurrency';
import { actionResetSalaryText } from 'src/app/state/modules/formData/salaryText';
import { actionUpdateCajaPayType } from 'src/app/state/modules/tealium/cajaPayType';
import { actionUpdateCajaSalaryCurrency } from 'src/app/state/modules/tealium/cajaSalaryCurrency';
import { getPayTypeForCountryCode } from 'src/app/utils/country';
import { getSalaryCurrency } from 'src/app/utils/salary/getSalary';
import { PayTypeToSalaryTypeMap } from 'src/app/utils/salary/utils/mappers';
import type AppState from 'src/types/appState';
import type { PayType } from 'src/types/salaryTypes';

export const useSyncCurrencyPayType = ({
  onResetSalary,
}: {
  onResetSalary: () => void;
}): {
  currency: SalaryCurrency;
  frequency: SalaryFrequency;
  payType: PayType;
} => {
  const {
    advertiserNation,
    formCurrency,
    formPayType,
    jobLocationCountry,
    jobLocationId,
    advertiserId,
  } = useSelector(
    (state: AppState) => ({
      advertiserNation: state.session.hirerAccount.nation,
      formCurrency: state.formData.salaryCurrency,
      formPayType: state.formData.payType,
      jobLocationCountry: state.session.jobLocationCountry,
      jobLocationId: state.formData.jobLocationId,
      advertiserId: state.session.advertiserId,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();
  const [currentJobLocationCountry, setCurrentJobLocationCountry] = useState<
    string | null
  >(jobLocationCountry);
  const [isLoadingJobLocationCountry, setIsLoadingJobLocationCountry] =
    useState<boolean>(false);
  const [currentCurrency, setCurrentCurrency] = useState<string | null>(
    formCurrency,
  );
  const [currentPayType, setCurrentPayType] = useState<string | null>(
    formPayType,
  );

  const defaultCurrency = getSalaryCurrency({
    formDataCurrency: null,
    jobLocationCountry,
    hirerNation: advertiserNation,
  });
  const defaultPayType = getPayTypeForCountryCode(
    jobLocationCountry || advertiserNation,
  );
  const currency = formCurrency ?? defaultCurrency;
  const payType = (formPayType ?? defaultPayType) as PayType;
  const frequency = PayTypeToSalaryTypeMap[payType];

  // If Job Location Country changes, then reset pay type and currency to match
  // the defaults of the new country (if they differ from redux state)
  useEffect(() => {
    const hasJobLocationId =
      typeof jobLocationId === 'string' && jobLocationId.length > 0;
    const hasJobLocationCountry =
      typeof jobLocationCountry === 'string' && jobLocationCountry.length > 0;

    // If an ID exists but no country code yet then we know its loading the data
    if (hasJobLocationId && !hasJobLocationCountry) {
      setIsLoadingJobLocationCountry(true);
    }

    if (
      hasJobLocationId &&
      hasJobLocationCountry &&
      currentJobLocationCountry?.toLowerCase() !==
        jobLocationCountry?.toLowerCase()
    ) {
      setCurrentJobLocationCountry(jobLocationCountry);

      // If we know it was simply loading the data then we don't
      // want to reset the pay type and currency. We allow this
      // state change to occur without side effects.
      if (isLoadingJobLocationCountry) {
        setIsLoadingJobLocationCountry(false);
        return;
      }

      if (formPayType !== defaultPayType) {
        dispatch(actionUpdatePayType(defaultPayType));
        dispatch(actionUpdateCajaPayType(defaultPayType));
      }

      if (formCurrency !== defaultCurrency) {
        dispatch(actionUpdateSalaryCurrency(defaultCurrency as SalaryCurrency));
        dispatch(actionUpdateCajaSalaryCurrency(defaultCurrency));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, jobLocationId, jobLocationCountry]);

  const isPHJobAUDCurrency = (newCurrency: string): boolean =>
    newCurrency === 'AUD' && advertiserNation === 'PH';

  // If the currency or pay type in redux changes then reset fields
  useEffect(() => {
    if (isPHJobAUDCurrency(formCurrency)) {
      datadogLogs.logger.warn(
        'Possible Location Currency Mismatch: PH Hirer / AUD Currency',
        {
          frequency,
          formPayType,
          payType,
          jobLocationCountry,
          formCurrency,
          jobLocationId,
          advertiserId,
        },
      );
    }

    const isEmptyPayType = !currentPayType;
    const hasChangedPayType = currentPayType !== formPayType;
    if (isEmptyPayType || hasChangedPayType) {
      setCurrentPayType(formPayType ? formPayType : payType);
      if (isEmptyPayType) {
        dispatch(actionUpdatePayType(payType));
        dispatch(actionUpdateCajaPayType(payType));
      }
    }

    const isEmptyCurrency = !currentCurrency;
    const hasChangedPayCurrency = currentCurrency !== formCurrency;
    if (isEmptyCurrency || hasChangedPayCurrency) {
      setCurrentCurrency(formCurrency ? formCurrency : currency);
      if (isEmptyCurrency) {
        dispatch(actionUpdateSalaryCurrency(currency));
        dispatch(actionUpdateCajaSalaryCurrency(currency));
      }
    }

    if (hasChangedPayType || hasChangedPayCurrency) {
      dispatch(actionResetMinSalary());
      dispatch(actionResetMaxSalary());
      dispatch(actionResetSalaryText());
      onResetSalary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formPayType, formCurrency]);

  return { currency, frequency, payType };
};
